import { arrayMove } from '@dnd-kit/sortable';
import { newEntityId } from '@store/api/poiTypeEndpoints';
import {
  reportDesignContentKeys, reportDesignLimits, reportNumberRanges, reportTextLengths, type ReportBlock,
  type ReportBlockType, type ReportChrome, type ReportDesign, type ReportDesignContent, type ReportDesignPage,
  type ReportText, type UpdateReportDesignInput,
} from '@store/api/reportDesignEndpoints';

/**
 * The report designer's working copy and every edit made to it.
 *
 * Each edit is a pure function from one draft to the next, so the designer component only
 * wires them to state. The draft has the shape the API stores, and is saved as one PUT.
 */

/** A design as the editor holds it: its content, and what it goes by. */
export interface ReportDesignDraft extends ReportDesignContent {
  name: string;
  /** Empty where there is none: a form holds text, the API `null`. */
  description: string;
}

/** The two lists of free pages a design has. */
export const pageSections = ['covers', 'closings'] as const;
export type PageSection = typeof pageSections[number];

/**
 * Something of a design an author can point at: what the editor has selected, and where an
 * issue is.
 */
export type DraftTarget =
  | { kind: 'details' | 'theme' | 'header' | 'footer' | 'summary' | 'checkpoints' }
  | { kind: 'page' | 'block'; id: string };

/** Whether two targets are the same part of a design. */
export const sameTarget = (a: DraftTarget, b: DraftTarget): boolean =>
  a.kind === b.kind && (!('id' in a) || !('id' in b) || a.id === b.id);

/** The seven content sections of anything that carries them, in the model's order. */
export const contentOf = (source: ReportDesignContent): ReportDesignContent =>
  Object.fromEntries(reportDesignContentKeys.map((key) => [key, source[key]])) as unknown as ReportDesignContent;

/** The name a design's writes are queued under (`useWriteChain`), for whoever opens it while some are on their way. */
export const designWrites = (designId: string): string => `report-design:${designId}`;

export const draftOf = (design: ReportDesign): ReportDesignDraft => ({
  name: design.name,
  description: design.description ?? '',
  ...contentOf(design),
});

/**
 * A design with its pages taken out and its header, footer and summary switched off: what
 * "Clear design" leaves, and exactly what its confirmation says it leaves. The API creates a
 * design only from a preset or from another design, so this is how one without a preset's
 * pages comes about. What is switched off keeps its settings, so switching it on again
 * brings them back; the checkpoints list and the theme are not touched at all.
 */
export const emptied = <D extends ReportDesignContent>(draft: D): D => ({
  ...draft,
  header: { ...draft.header, enabled: false },
  footer: { ...draft.footer, enabled: false },
  covers: [],
  summary: { ...draft.summary, template: 'none' },
  closings: [],
});

// ---- Texts and numbers of a block ----

const { line, heading, paragraph } = reportTextLengths;

/**
 * Where each block type keeps a text of its own, and how long one locale of it may be: the
 * request schema's limits. The editor's block catalog takes its lengths from here, so a field
 * and the check on it cannot disagree. Texts inside a list (a field's label, a signature
 * line) are not here: they are `listTextLimits`.
 */
export const blockTextLimits: Record<ReportBlockType, Record<string, number>> = {
  hero: { overline: line, title: heading, subtitle: heading },
  heading: { text: heading },
  text: { content: paragraph },
  fields: {},
  image: {},
  photoGrid: {},
  signatures: {},
  divider: {},
  spacer: {},
};

/** The label over a value of a `fields` block, and the label of a signature line. */
export const listTextLimits = { fieldLabel: reportTextLengths.label, signatureLabel: reportTextLengths.label } as const;

/** The numbers a block holds and the range the request schema keeps each in. */
export const blockNumberRanges: Record<ReportBlockType, Record<string, { min: number; max: number }>> = {
  hero: {},
  heading: {},
  text: { size: reportNumberRanges.textSize },
  fields: {},
  image: { widthPercent: reportNumberRanges.widthPercent },
  photoGrid: {},
  signatures: {},
  divider: {},
  spacer: { height: reportNumberRanges.spacerHeight },
};

// ---- The request boundary ----

/**
 * A text without the locales that hold nothing, or `undefined` when none holds anything.
 *
 * A locale left blank is how an input says "no text here", and the report reads it that way
 * too: it skips a blank value and falls back as if the locale were absent. Sending it would
 * only spend the design's byte budget on it.
 */
export const cleanText = (text: ReportText | undefined): ReportText | undefined => {
  const entries = Object.entries(text ?? {}).filter(([, value]) => typeof value === 'string' && value.trim() !== '');

  return entries.length ? Object.fromEntries(entries) : undefined;
};

/** `source` with each of its `keys` cleaned as a text, and taken off where nothing is left of it. */
const withCleanTexts = <T extends object>(source: T, keys: readonly string[]): T => {
  const result = { ...source } as Record<string, unknown>;

  for (const key of keys) {
    if (!(key in result)) continue;

    const text = cleanText(result[key] as ReportText | undefined);

    if (text) result[key] = text;
    else delete result[key];
  }

  return result as T;
};

const normalizeBlock = (block: ReportBlock): ReportBlock => {
  if (!block.props) return block;

  // A block of a type this version does not know is sent back as it was read.
  const props: Record<string, unknown> = withCleanTexts(block.props, Object.keys(blockTextLimits[block.type] ?? {}));

  if (block.type === 'fields' && block.props.items) {
    props.items = block.props.items.map((item) => withCleanTexts(item, ['label']));
  }

  // A signature line's label is required by the request schema, so an empty one stays as `{}`.
  if (block.type === 'signatures' && block.props.lines) {
    props.lines = block.props.lines.map((signature) => ({ ...signature, label: cleanText(signature.label) ?? {} }));
  }

  return { ...block, props } as ReportBlock;
};

const normalizePage = (page: ReportDesignPage): ReportDesignPage => ({ ...page, blocks: page.blocks.map(normalizeBlock) });

const normalizeChrome = (chrome: ReportChrome): ReportChrome => {
  const result = { ...chrome };

  for (const side of ['left', 'center', 'right'] as const) {
    const slot = result[side];

    if (slot) result[side] = withCleanTexts(slot, ['text']);
  }

  return result;
};

/**
 * The content as it is sent: the seven sections and nothing else, every text without its
 * empty locales. Applied where a request is built, never to the draft itself, so an input
 * the author has just emptied stays an input.
 */
export const normalizeContent = (content: ReportDesignContent): ReportDesignContent => ({
  theme: withCleanTexts(content.theme, ['brandName']),
  header: normalizeChrome(content.header),
  footer: normalizeChrome(content.footer),
  covers: content.covers.map(normalizePage),
  summary: withCleanTexts(content.summary, ['title']),
  checkpoints: withCleanTexts(content.checkpoints, ['title']),
  closings: content.closings.map(normalizePage),
});

/** A draft as the body of a save, but for the version the save claims. */
export const toReportDesignBody = (draft: ReportDesignDraft): Omit<UpdateReportDesignInput, 'id' | 'version'> => ({
  name: draft.name.trim(),
  description: draft.description.trim() || null,
  ...normalizeContent(draft),
});

/**
 * How large the content is as the server measures it: the seven sections of what would be
 * sent, as compact JSON, in bytes of UTF-8. The server refuses a save over
 * `reportDesignLimits.bytes`, since every request body stops at 100 kB.
 */
export const contentBytes = (content: ReportDesignContent): number =>
  new TextEncoder().encode(JSON.stringify(normalizeContent(content))).length;

// ---- Reading a draft ----

export const pagesOf = (content: ReportDesignContent): ReportDesignPage[] => [...content.covers, ...content.closings];

export interface PagePosition {
  section: PageSection;
  index: number;
  page: ReportDesignPage;
}

export const locatePage = (content: ReportDesignContent, pageId: string): PagePosition | null => {
  for (const section of pageSections) {
    const index = content[section].findIndex((page) => page.id === pageId);

    if (index >= 0) return { section, index, page: content[section][index] };
  }

  return null;
};

export interface BlockPosition {
  page: ReportDesignPage;
  index: number;
  block: ReportBlock;
}

export const locateBlock = (content: ReportDesignContent, blockId: string): BlockPosition | null => {
  for (const page of pagesOf(content)) {
    const index = page.blocks.findIndex((block) => block.id === blockId);

    if (index >= 0) return { page, index, block: page.blocks[index] };
  }

  return null;
};

/**
 * The page a block goes to when it is added without being put somewhere: the page that is
 * selected, or the one that holds the selected block, or else the first page the design has,
 * a cover before a closing page. None where the design has no page at all.
 */
export const pageForNewBlock = (content: ReportDesignContent, selection: DraftTarget): ReportDesignPage | undefined => {
  const selected = selection.kind === 'page'
    ? locatePage(content, selection.id)?.page
    : selection.kind === 'block' ? locateBlock(content, selection.id)?.page : undefined;

  return selected ?? pagesOf(content)[0];
};

/** Whether what is selected is still in the draft: a page or a block can be deleted from under it. */
export const targetExists = (content: ReportDesignContent, target: DraftTarget): boolean => {
  if (target.kind === 'page') return locatePage(content, target.id) !== null;
  if (target.kind === 'block') return locateBlock(content, target.id) !== null;

  return true;
};

/** An image a design names, and the block that names it: `null` for the theme's logo. */
export interface ImageReference {
  imageId: string;
  blockId: string | null;
}

/**
 * Every image the content refers to: the theme's logo and each image block's upload, whatever
 * the block draws at the moment. The server asks that each of them exists, in the same terms.
 */
export const imageReferencesOf = (content: ReportDesignContent): ImageReference[] => [
  ...(content.theme.logo ? [{ imageId: content.theme.logo.imageId, blockId: null }] : []),
  ...pagesOf(content).flatMap((page) => page.blocks.flatMap((block) => (
    block.type === 'image' && block.props?.image ? [{ imageId: block.props.image.imageId, blockId: block.id }] : []
  ))),
];

// ---- What stands in the way of a save ----

export type DraftIssue = { target: DraftTarget } & (
  /** The design has no name. */
  | { kind: 'name' }
  /** More cover or closing pages than a design may have. */
  | { kind: 'pages'; section: PageSection; limit: number }
  /** More blocks on a page than it may hold. */
  | { kind: 'blocks'; limit: number }
  /** More values in a `fields` block, or more signature lines, than one may hold. */
  | { kind: 'items'; limit: number }
  /** A text longer, in some locale, than its place allows. */
  | { kind: 'text'; limit: number }
  /** A number outside its range. */
  | { kind: 'number'; min: number; max: number }
  /** The accent is not a `#rrggbb` colour. */
  | { kind: 'color' }
  /** An image the server no longer has: a save would be refused until it is uploaded again or taken off. */
  | { kind: 'image'; imageId: string }
  /** The content as a whole is over the byte budget. */
  | { kind: 'bytes'; bytes: number; limit: number }
);

/**
 * Whether an issue is about what the design goes by, its name or its description. A preview
 * carries neither, so such an issue holds back a save and nothing else.
 */
export const isNamingIssue = (issue: DraftIssue): boolean =>
  issue.kind === 'name' || (issue.kind === 'text' && issue.target.kind === 'details');

const isTooLong = (text: ReportText | undefined, limit: number): boolean =>
  Object.values(text ?? {}).some((value) => typeof value === 'string' && value.length > limit);

const ACCENT = /^#[0-9a-fA-F]{6}$/;

const blockIssues = (block: ReportBlock): DraftIssue[] => {
  const target: DraftTarget = { kind: 'block', id: block.id };
  const props: Record<string, unknown> = block.props ?? {};
  const issues: DraftIssue[] = [];

  for (const [key, limit] of Object.entries(blockTextLimits[block.type] ?? {})) {
    if (isTooLong(props[key] as ReportText | undefined, limit)) issues.push({ kind: 'text', target, limit });
  }

  for (const [key, { min, max }] of Object.entries(blockNumberRanges[block.type] ?? {})) {
    const value = props[key];

    if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max)) {
      issues.push({ kind: 'number', target, min, max });
    }
  }

  if (block.type === 'fields') {
    const items = block.props?.items ?? [];

    if (items.length > reportDesignLimits.fieldItems) {
      issues.push({ kind: 'items', target, limit: reportDesignLimits.fieldItems });
    }
    if (items.some((item) => isTooLong(item.label, listTextLimits.fieldLabel))) {
      issues.push({ kind: 'text', target, limit: listTextLimits.fieldLabel });
    }
  }

  if (block.type === 'signatures') {
    const lines = block.props?.lines ?? [];

    if (lines.length > reportDesignLimits.signatureLines) {
      issues.push({ kind: 'items', target, limit: reportDesignLimits.signatureLines });
    }
    if (lines.some((signature) => isTooLong(signature.label, listTextLimits.signatureLabel))) {
      issues.push({ kind: 'text', target, limit: listTextLimits.signatureLabel });
    }
  }

  return issues;
};

const chromeIssues = (chrome: ReportChrome, kind: 'header' | 'footer'): DraftIssue[] =>
  ([chrome.left, chrome.center, chrome.right].some((slot) => isTooLong(slot?.text, reportTextLengths.line))
    ? [{ kind: 'text', target: { kind }, limit: reportTextLengths.line }]
    : []);

/**
 * What the server would refuse in a draft, found before it is sent: the limits of the
 * request schema, the byte budget, and a reference to an image that is known to be gone.
 * A draft with an issue is neither saved nor previewed, except that an issue with its name
 * holds back only the save.
 *
 * `missingImages` are the ids the server has answered for with "no such image".
 */
export const draftIssues = (draft: ReportDesignDraft, missingImages: ReadonlySet<string> = new Set()): DraftIssue[] => {
  const issues: DraftIssue[] = [];
  const details: DraftTarget = { kind: 'details' };

  if (!draft.name.trim()) issues.push({ kind: 'name', target: details });
  if (draft.name.trim().length > reportDesignLimits.nameLength) {
    issues.push({ kind: 'text', target: details, limit: reportDesignLimits.nameLength });
  }
  if (draft.description.trim().length > reportDesignLimits.descriptionLength) {
    issues.push({ kind: 'text', target: details, limit: reportDesignLimits.descriptionLength });
  }

  if (isTooLong(draft.theme.brandName, reportTextLengths.brand)) {
    issues.push({ kind: 'text', target: { kind: 'theme' }, limit: reportTextLengths.brand });
  }
  if (draft.theme.accentColor !== undefined && !ACCENT.test(draft.theme.accentColor)) {
    issues.push({ kind: 'color', target: { kind: 'theme' } });
  }

  issues.push(...chromeIssues(draft.header, 'header'), ...chromeIssues(draft.footer, 'footer'));

  for (const kind of ['summary', 'checkpoints'] as const) {
    if (isTooLong(draft[kind].title, reportTextLengths.line)) {
      issues.push({ kind: 'text', target: { kind }, limit: reportTextLengths.line });
    }
  }

  for (const section of pageSections) {
    const pages = draft[section];

    if (pages.length > reportDesignLimits.pages) {
      issues.push({
        kind: 'pages', section, limit: reportDesignLimits.pages, target: { kind: 'page', id: pages[pages.length - 1].id },
      });
    }

    for (const page of pages) {
      if (page.blocks.length > reportDesignLimits.blocksPerPage) {
        issues.push({ kind: 'blocks', target: { kind: 'page', id: page.id }, limit: reportDesignLimits.blocksPerPage });
      }

      issues.push(...page.blocks.flatMap(blockIssues));
    }
  }

  for (const { imageId, blockId } of imageReferencesOf(draft)) {
    if (missingImages.has(imageId)) {
      issues.push({ kind: 'image', imageId, target: blockId === null ? { kind: 'theme' } : { kind: 'block', id: blockId } });
    }
  }

  const bytes = contentBytes(draft);

  if (bytes > reportDesignLimits.bytes) {
    issues.push({ kind: 'bytes', bytes, limit: reportDesignLimits.bytes, target: { kind: 'details' } });
  }

  return issues;
};

// ---- Edits ----

const withoutUndefined = <T extends object>(source: T): T =>
  Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined)) as T;

/** The parts of a design that are one object each: everything but its two lists of pages. */
export type DraftSection = 'theme' | 'header' | 'footer' | 'summary' | 'checkpoints';

/** Sets keys of one section. `undefined` takes a key off, and the report draws its default for it. */
export const patchSection = <D extends ReportDesignContent, K extends DraftSection>(
  draft: D, section: K, changes: Partial<ReportDesignContent[K]>,
): D => ({ ...draft, [section]: withoutUndefined({ ...draft[section], ...changes }) });

/** A page as it is added: empty, drawn with the defaults of the list it goes into. */
export const newPage = (): ReportDesignPage => ({ id: newEntityId(), blocks: [] });

export const addPage = <D extends ReportDesignContent>(draft: D, section: PageSection, page: ReportDesignPage): D =>
  ({ ...draft, [section]: [...draft[section], page] });

const withPage = <D extends ReportDesignContent>(
  draft: D, pageId: string, change: (page: ReportDesignPage) => ReportDesignPage,
): D => {
  const at = locatePage(draft, pageId);

  if (!at) return draft;

  return { ...draft, [at.section]: draft[at.section].map((page) => (page.id === pageId ? change(page) : page)) };
};

export const removePage = <D extends ReportDesignContent>(draft: D, pageId: string): D => {
  const at = locatePage(draft, pageId);

  return at ? { ...draft, [at.section]: draft[at.section].filter((page) => page.id !== pageId) } : draft;
};

/** Moves a page within its own list: a cover stays a cover. */
export const movePage = <D extends ReportDesignContent>(draft: D, pageId: string, to: number): D => {
  const at = locatePage(draft, pageId);

  if (!at || to < 0 || to >= draft[at.section].length) return draft;

  return { ...draft, [at.section]: arrayMove(draft[at.section], at.index, to) };
};

/** Sets a page's own options. `undefined` takes one off, which is the list's default again. */
export const patchPage = <D extends ReportDesignContent>(
  draft: D, pageId: string, changes: Partial<Pick<ReportDesignPage, 'background' | 'verticalAlign'>>,
): D => withPage(draft, pageId, (page) => withoutUndefined({ ...page, ...changes }));

/** `index` defaults to the end of the page. */
export const insertBlock = <D extends ReportDesignContent>(
  draft: D, pageId: string, block: ReportBlock, index?: number,
): D => {
  const insert = (page: ReportDesignPage): ReportDesignPage => {
    const blocks = [...page.blocks];

    blocks.splice(index ?? blocks.length, 0, block);

    return { ...page, blocks };
  };

  return withPage(draft, pageId, insert);
};

export const removeBlock = <D extends ReportDesignContent>(draft: D, blockId: string): D => {
  const at = locateBlock(draft, blockId);

  return at
    ? withPage(draft, at.page.id, (page) => ({ ...page, blocks: page.blocks.filter((block) => block.id !== blockId) }))
    : draft;
};

/** Moves a block within its page. */
export const moveBlock = <D extends ReportDesignContent>(draft: D, pageId: string, from: number, to: number): D =>
  withPage(draft, pageId, (page) => (
    from < 0 || to < 0 || from >= page.blocks.length || to >= page.blocks.length
      ? page
      : { ...page, blocks: arrayMove(page.blocks, from, to) }
  ));

/**
 * Moves a block to `index` of a page, its own or another: the place it has among that page's
 * blocks once it is there. The block is taken out and put in within one edit, so there is no
 * draft between the two in which it is on neither page or on both.
 *
 * A page that is full takes no block from another page. A block or a page that is not in the
 * draft, and a place the page does not have, change nothing.
 */
export const moveBlockTo = <D extends ReportDesignContent>(draft: D, blockId: string, pageId: string, index: number): D => {
  const from = locateBlock(draft, blockId);
  const to = locatePage(draft, pageId);

  if (!from || !to || index < 0) return draft;

  if (from.page.id === pageId) {
    return index === from.index || index >= to.page.blocks.length ? draft : moveBlock(draft, pageId, from.index, index);
  }

  if (index > to.page.blocks.length || to.page.blocks.length >= reportDesignLimits.blocksPerPage) return draft;

  const moved = (page: ReportDesignPage): ReportDesignPage => {
    if (page.id === from.page.id) return { ...page, blocks: page.blocks.filter((block) => block.id !== blockId) };
    if (page.id !== pageId) return page;

    const blocks = [...page.blocks];

    blocks.splice(index, 0, from.block);

    return { ...page, blocks };
  };

  return { ...draft, covers: draft.covers.map(moved), closings: draft.closings.map(moved) };
};

/** A place among the blocks of a page: what `moveBlockTo` takes. */
export interface BlockPlace {
  pageId: string;
  index: number;
}

/**
 * Where a block stands after one step up (`-1`) or down (`1`): the next place on its page,
 * or, from the edge of its page, the end of the page before or the start of the page after,
 * in the order the pages print. A page that is full takes no block, and the step goes on past
 * it to the next page that has room, as a drag does. So every place a block can be dragged
 * to can be reached a step at a time. `null` where there is no step to take: at either end
 * of the design, or where every page from the block's own to that end is full.
 */
export const blockStep = (content: ReportDesignContent, blockId: string, by: -1 | 1): BlockPlace | null => {
  const at = locateBlock(content, blockId);

  if (!at) return null;

  const index = at.index + by;

  if (index >= 0 && index < at.page.blocks.length) return { pageId: at.page.id, index };

  const pages = pagesOf(content);

  for (let nth = pages.indexOf(at.page) + by; nth >= 0 && nth < pages.length; nth += by) {
    const next = pages[nth];

    if (next.blocks.length < reportDesignLimits.blocksPerPage) {
      return { pageId: next.id, index: by < 0 ? next.blocks.length : 0 };
    }
  }

  return null;
};

/**
 * Sets keys of a block's `props`. `undefined` takes a key off, and the block then draws its
 * default for it: nothing here writes a default into the document, where it would freeze
 * today's value into the design.
 */
export const patchBlock = <D extends ReportDesignContent>(draft: D, blockId: string, changes: Record<string, unknown>): D => {
  const at = locateBlock(draft, blockId);

  if (!at) return draft;

  return withPage(draft, at.page.id, (page) => ({
    ...page,
    blocks: page.blocks.map((block) => (block.id === blockId
      ? { ...block, props: withoutUndefined({ ...(block.props ?? {}), ...changes }) } as ReportBlock
      : block)),
  }));
};

/** A copy of a block under an id of its own, sharing nothing with the original. */
export const copyOfBlock = (block: ReportBlock): ReportBlock => ({
  ...(JSON.parse(JSON.stringify(block)) as ReportBlock),
  id: newEntityId(),
});

// ---- Images ----

/**
 * The pixels a PNG's header says it has, from the file's first 24 bytes: the width and the
 * height are the two numbers that follow the signature and the header chunk's own eight bytes.
 */
export const pngPixelsOf = (bytes: Uint8Array): number => {
  const number = (at: number) => ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;

  return number(16) * number(20);
};

/**
 * What an image file is, by its first bytes: the server's own test, so a file it would
 * refuse is not sent. A name or a declared type says only what its sender believes.
 */
export const sniffImage = (bytes: Uint8Array): 'png' | 'jpg' | null => {
  const startsWith = (signature: number[]) => signature.every((byte, index) => bytes[index] === byte);

  if (startsWith([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (startsWith([0xff, 0xd8, 0xff])) return 'jpg';

  return null;
};
